import React, { useEffect, useState } from 'react';
import { FlatList, KeyboardAvoidingView } from 'react-native';

import { ProjectTagList } from '@components/Chat/ProjectTagList';
import { TaskTagList } from '@components/Chat/TaskTagList';
import EmptySearchNoTask from '@components/EmptyState/EmptySearchNoTask';
import EmptyStateNoProjectTag from '@components/EmptyState/EmptyStateNoProjectTag';
import { Box } from '@components/Restyle';
import SearchLoadMore from '@components/Search/SearchLoadMore';
import { FadeInView } from '@components/shared/FadeInView';
import { ListLoadingFooter } from '@components/shared/ListSpacers';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { SkeletonList } from '@components/shared/skeletons/SkeletonList';
import { TagRowSkeleton } from '@components/shared/skeletons/TagRowSkeleton';
import {
  SearchableResult,
  Chat,
  useSearchTagsLazyQuery,
  Project,
  Task,
} from '@graphql/generated';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import { useListChatProjectsTasksFromQuery } from '@hooks/useListChatProjectsTasksFromQuery';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import useTagInput from '@hooks/useTagInput';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { Search, SearchResultType } from '@root/types';
import { ProjectTagType } from '@src/types/project';
import { TaskTagType } from '@src/types/task';
import { isProjectSearchResult, isTaskSearchResult } from '@utils/typeGuards';

const PAGESIZE = 20;

const renderTagRowSkeleton = () => <TagRowSkeleton />;

interface ChatRelatedTagsContentProps {
  bottomInset?: number;
  filterVal: string;
  chatId: Chat['id'];
  isFrom: string;
}

const PER_PAGE_COUNT = 10;

export const ChatRelatedTagsContent: React.FC<ChatRelatedTagsContentProps> = ({
  chatId,
  isFrom,
}) => {
  const [filterVal, setFilterVal] = useState('');

  const { tags } = useTagInput();

  const tagsCollection = tags(chatId);

  const tagedTasksIds: string[] = [];
  tagsCollection.forEach((t2) =>
    t2.tasks.forEach((t3) => tagedTasksIds.push(t3.id))
  );

  const {
    projectsTasks: allProjectsTasks,
    refetch: refetchProjects,
    refreshing: refreshingProjects,
    setRefreshing: setRefreshingProjects,
    pageInfo,
    fetchMoreFromCursor: fetchMoreProjectsFromCursor,
    loading: allProjectsTasksLoading,
  } = useListChatProjectsTasksFromQuery({
    first: PER_PAGE_COUNT,
    chatId: chatId,
  });

  const { navigateToTask, navigateToProject } = useAppNavigation();
  const navigateToProjectDetails = (p: Pick<Project, 'id'>) => {
    navigateToProject(p);
  };

  const navigateToTaskDetails = (t: Pick<Task, 'id' | 'projectId'>) => {
    navigateToTask(t);
  };

  const projectsTasksData = allProjectsTasks;

  const [globalSearch, { data, loading: loadingSearch }] =
    useSearchTagsLazyQuery();
  const searchData = (data?.search as Search<SearchableResult>[]) || [];
  const initialResults = {
    messages: [],
    tasks: [],
    documents: [],
    projects: [],
    contacts: [],
  };

  const [searchResultsPage, setSearchResultsPage] = useState(initialResults);
  const [isProjectLength, setProjectLength] = useState(0);

  const getLoadMore = (results: SearchResultType) => {
    if (
      results.projects.length > 0 &&
      results.projects.length < results.projects[0].total
    ) {
      setProjectLength(results.projects.length);
    } else {
      setProjectLength(0);
    }
  };

  useEffect(() => {
    const results = searchData.reduce<SearchResultType>((previous, result) => {
      if (
        previous.projects.some(
          (existingResult) => existingResult.record.id === result.record.id
        )
      )
        return previous;
      if (isProjectSearchResult(result))
        return { ...previous, projects: [...previous.projects, result] };
      if (
        previous.tasks.some(
          (existingResult) => existingResult.record.id === result.record.id
        )
      )
        return previous;
      if (isTaskSearchResult(result))
        return { ...previous, tasks: [...previous.tasks, result] };
    }, searchResultsPage);
    setSearchResultsPage(results);
    getLoadMore(results);
  }, [searchData]);

  const [projectResults, setProjectResults] = useState([]);
  useEffect(() => {
    const searchResultsProject = searchResultsPage.projects
      ? searchResultsPage.projects.map((r) => r.record)
      : [];

    setProjectResults(searchResultsProject);
  }, [searchResultsPage]);

  const [isLoadMore, setIsLoadMore] = useState(false);
  const from = isProjectLength;

  // "Load more" pagination — takes the already-settled, trimmed term (from
  // useDebouncedSearch — NOT the raw filterVal) as an explicit argument
  // rather than closing over `gatedTerm` from the hook destructured further
  // below, so this helper doesn't depend on declaration order (TA-10169).
  // Using the same trimmed term as page 0 keeps every page of one paginated
  // search on the same backend query.
  const loadMoreCall = (term: string) => {
    if (!term) return;
    globalSearch({
      variables: {
        term,
        from: from,
        size: PAGESIZE,
        includeMessages: false,
        includeDocuments: false,
        includeProjects: true,
        includeTasks: false,
        includeChats: false,
        includeTasksTags: true,
        includeCompletedTask: true,
        chatId: chatId,
      },
    });
  };

  const globalSearchCall = (term: string) => {
    globalSearch({
      variables: {
        term,
        from: 0,
        size: PAGESIZE,
        includeMessages: false,
        includeDocuments: false,
        includeProjects: true,
        includeTasks: false,
        includeChats: false,
        includeTasksTags: true,
        includeCompletedTask: true,
        chatId: chatId,
      },
    });
  };

  const { isPending, isBelowThreshold, hasSearched, gatedTerm } =
    useDebouncedSearch(filterVal, globalSearchCall);
  // isPending settles to false one render before onSearch actually fires —
  // hasSearched closes that gap so the empty state can't flash before the
  // query is kicked off (TA-10091).
  const isSearchLoading =
    !isBelowThreshold && (loadingSearch || isPending || !hasSearched);

  // hasSearched-gated so the previous term's results (still sitting in the
  // projectResults accumulator until the [filterVal] effect below clears it)
  // can't render as live/tappable during the pending window between a
  // keystroke and the debounce settling for a new above-threshold term
  // (TA-10169).
  const showSearchResults =
    !isBelowThreshold && hasSearched && projectResults.length !== 0;

  useEffect(() => {
    if (data) {
      setIsLoadMore(true);
    }
  }, [data]);

  useEffect(() => {
    setSearchResultsPage(initialResults);
    setProjectLength(0);
    setIsLoadMore(false);
  }, [filterVal]);

  const renderLoadMoreComponent = () => {
    if (isProjectLength > 0) {
      return (
        <SearchLoadMore
          loading={loadingSearch && isLoadMore}
          onLoadMore={() => loadMoreCall(gatedTerm)}
        />
      );
    }
    return <Box mb='s' />;
  };

  const renderItem = (item: Project) => {
    return (
      <Box>
        <ProjectTagList
          key={item.id}
          refreshing={refreshingProjects}
          loading={false}
          isFrom={isFrom}
          onRefresh={() => {
            setRefreshingProjects(true);
            refetchProjects();
          }}
          filterVal={filterVal}
          projectTags={[item]}
          onSelect={(newProject: ProjectTagType) =>
            navigateToProjectDetails(newProject)
          }
        />

        {item?.tasks?.map((taskObj) => {
          return (
            <TaskTagList
              key={taskObj.id}
              filterVal={filterVal}
              taskTags={[taskObj]}
              showMember
              onSelect={(newTask: TaskTagType) =>
                navigateToTaskDetails(newTask)
              }
              loading={false}
            />
          );
        })}
      </Box>
    );
  };

  const renderSearch = (item: Project) => {
    return (
      <Box>
        <ProjectTagList
          key={item.id}
          refreshing={refreshingProjects}
          loading={false}
          isFrom={isFrom}
          onRefresh={() => {
            setRefreshingProjects(true);
            refetchProjects();
          }}
          filterVal={filterVal}
          projectTags={[item]}
          onSelect={(newProject: ProjectTagType) =>
            navigateToProjectDetails(newProject)
          }
        />

        {item?.tasksTagList?.map((taskObj) => {
          return (
            <TaskTagList
              key={taskObj.id}
              filterVal={filterVal}
              taskTags={[taskObj]}
              showMember
              onSelect={(newTask: TaskTagType) => {
                navigateToTaskDetails(newTask);
              }}
              loading={false}
            />
          );
        })}
      </Box>
    );
  };

  const showEmptyState = projectsTasksData.every((i) => i.data?.length === 0);
  const showNoTask =
    !isBelowThreshold && !isSearchLoading && projectResults.length === 0;

  // TA-10279: 3 skeleton rows while recent items fetch — held for a minimum
  // 150ms and scoped to "no rows visible yet" so an in-flight fetchMore
  // (rows already on screen) never gets its content swapped for a skeleton.
  const showBrowseSkeleton = useMinimumVisible(
    isBelowThreshold &&
      allProjectsTasksLoading &&
      projectsTasksData.length === 0
  );
  // Same rule for the search branch — isSearchLoading already folds in the
  // debounce window (useDebouncedSearch's isPending/hasSearched).
  const showTagSearchSkeleton = useMinimumVisible(
    !isBelowThreshold && isSearchLoading && !showSearchResults
  );

  return (
    <Box
      backgroundColor='white'
      style={{
        flex: 1,
        height: '100%',
      }}>
      <Box marginTop='xs' paddingHorizontal='m'>
        <Box marginTop='m' flexDirection='row' alignItems='center'>
          <SearchInput
            onTextChanged={(v) => {
              setFilterVal(v);
            }}
            onCancel={() => setFilterVal('')}
            value={filterVal}
            autoFocus
            isTagSearch={true}
            isChatRelatedTagSearch={true}
          />
        </Box>
      </Box>
      <KeyboardAvoidingView style={{ flex: 1, paddingBottom: 10 }}>
        {showBrowseSkeleton ? (
          <SkeletonList count={3} row={renderTagRowSkeleton} />
        ) : (
          <>
            {showEmptyState && !allProjectsTasksLoading && isBelowThreshold && (
              <EmptyStateNoProjectTag />
            )}
            {showNoTask && <EmptySearchNoTask isFromChatTags={true} />}
            {projectsTasksData.length !== 0 &&
              isBelowThreshold &&
              !showEmptyState && (
                <FadeInView style={{ flex: 1 }}>
                  <FlatList
                    data={projectsTasksData}
                    keyboardShouldPersistTaps='always'
                    showsVerticalScrollIndicator={false}
                    renderItem={({ item }) => {
                      return <>{renderItem(item)}</>;
                    }}
                    initialNumToRender={10}
                    ListFooterComponent={
                      <ListLoadingFooter
                        isLoading={pageInfo?.hasNextPage || false}
                      />
                    }
                    onEndReached={() => {
                      fetchMoreProjectsFromCursor();
                    }}
                  />
                </FadeInView>
              )}
          </>
        )}
        {showTagSearchSkeleton ? (
          <SkeletonList count={3} row={renderTagRowSkeleton} />
        ) : (
          showSearchResults && (
            <FadeInView style={{ flex: 1 }}>
              <FlatList
                data={projectResults}
                ListFooterComponent={renderLoadMoreComponent()}
                keyboardShouldPersistTaps='always'
                showsVerticalScrollIndicator={false}
                renderItem={({ item }) => {
                  return <>{renderSearch(item)}</>;
                }}
              />
            </FadeInView>
          )
        )}
      </KeyboardAvoidingView>
    </Box>
  );
};
